import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import {
  Shield, AlertTriangle, Clock, Loader2, Package, TrendingDown,
  CloudRain, Sun, Wind, DollarSign, Activity, RefreshCw, ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const SCORE_COLORS = { Excellent: "#10B981", Bon: "#0EA5E9", Attention: "#F59E0B", Critique: "#EF4444" };
const SEASON_ICONS = { demand_high: CloudRain, demand_low: Sun };

const SentinelPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sentinel/dashboard`, { headers });
      setData(res.data);
    } catch (err) { toast.error("Erreur de chargement Sentinel"); }
    finally { setLoading(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div></Layout>;
  if (!data) return <Layout><div className="p-6 text-center text-slate-500">Aucune donnée disponible</div></Layout>;

  const scoreColor = SCORE_COLORS[data.health_label] || "#64748b";

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="sentinel-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-white flex items-center gap-2">
              <Activity className="w-7 h-7 text-cyan-500" />
              Sentinel — Alertes Prédictives
            </h1>
            <p className="text-slate-500 text-sm mt-1">Anticipez les risques et optimisez votre gestion</p>
          </div>
          <Button onClick={load} variant="outline" className="border-cyan-500/20 text-cyan-400">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* Health Score */}
        <Card className="medical-card overflow-hidden" data-testid="health-score-card">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              <div className="flex-shrink-0 p-8 flex flex-col items-center justify-center" style={{ background: `linear-gradient(135deg, ${scoreColor}15, ${scoreColor}08)` }}>
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                    <circle cx="60" cy="60" r="50" fill="none" stroke={scoreColor} strokeWidth="10" strokeDasharray={`${data.health_score * 3.14} 314`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold" style={{ color: scoreColor }}>{data.health_score}</span>
                    <span className="text-xs text-slate-500">/100</span>
                  </div>
                </div>
                <p className="mt-2 font-semibold text-lg" style={{ color: scoreColor }}>{data.health_label}</p>
              </div>
              <div className="flex-1 p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Produits actifs", value: data.summary.total_products, icon: Package, color: "cyan" },
                  { label: "Périmés", value: data.summary.expired, icon: AlertTriangle, color: "red" },
                  { label: "Expirent sous 30j", value: data.summary.expiring_30d, icon: Clock, color: "amber" },
                  { label: "Expirent sous 90j", value: data.summary.expiring_90d, icon: Clock, color: "orange" },
                ].map((kpi, i) => (
                  <div key={i} className="text-center">
                    <kpi.icon className={`w-5 h-5 text-${kpi.color}-500 mx-auto mb-1`} />
                    <p className={`text-2xl font-bold text-${kpi.color}-600`}>{kpi.value}</p>
                    <p className="text-xs text-slate-500">{kpi.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expiry Timeline */}
          <Card className="medical-card" data-testid="expiry-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Chronologie des expirations</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.expiry_timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8 }} formatter={(v, n) => [n === "count" ? `${v} produits` : `${v.toLocaleString()} F`, n === "count" ? "Produits" : "Valeur"]} />
                  <Bar dataKey="count" name="Produits" fill="#F59E0B" radius={[4, 4, 0, 0]}>
                    {data.expiry_timeline.map((_, i) => (
                      <Cell key={i} fill={["#EF4444", "#F59E0B", "#F97316", "#0EA5E9"][i] || "#64748b"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Seasonal Alerts */}
          <Card className="medical-card" data-testid="seasonal-alerts">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Wind className="w-5 h-5 text-cyan-500" />
                Alertes saisonnières
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.seasonal_alerts.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucune alerte saisonnière active</p>
              ) : data.seasonal_alerts.map((alert, i) => {
                const Icon = SEASON_ICONS[alert.type] || Wind;
                return (
                  <div key={i} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800">{alert.message}</p>
                        <p className="text-xs text-amber-400 mt-1">Période: {alert.months}</p>
                        <div className="flex items-center gap-1 mt-2">
                          <ChevronRight className="w-3 h-3 text-amber-500" />
                          <span className="text-xs text-amber-400 font-medium">{alert.action}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Cash Flow Alerts */}
        {data.cash_alerts.length > 0 && (
          <div className="space-y-3">
            {data.cash_alerts.map((alert, i) => (
              <Card key={i} className="border-red-500/20 bg-red-500/10" data-testid={`cash-alert-${i}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <DollarSign className="w-6 h-6 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">{alert.title}</p>
                    <p className="text-sm text-red-400">{alert.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Risk Products */}
        {data.risk_products.length > 0 && (
          <Card className="medical-card" data-testid="risk-products">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Produits à risque (Top 10 par valeur)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-slate-500">
                      <th className="text-left py-2 px-2">Produit</th>
                      <th className="text-left py-2 px-2">Catégorie</th>
                      <th className="text-right py-2 px-2">Qté</th>
                      <th className="text-right py-2 px-2">Valeur</th>
                      <th className="text-right py-2 px-2">Expiration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.risk_products.map((p, i) => {
                      const daysLeft = Math.ceil((new Date(p.expiry_date) - new Date()) / 86400000);
                      return (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-2 px-2 font-medium text-slate-200">{p.name}</td>
                          <td className="py-2 px-2 text-slate-500">{p.category}</td>
                          <td className="py-2 px-2 text-right">{p.quantity}</td>
                          <td className="py-2 px-2 text-right text-amber-400 font-medium">{p.value.toLocaleString()} F</td>
                          <td className="py-2 px-2 text-right">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${daysLeft <= 30 ? "bg-red-500/10 text-red-400" : daysLeft <= 90 ? "bg-amber-500/10 text-amber-400" : "bg-blue-500/10 text-blue-400"}`}>
                              {daysLeft <= 0 ? "Expiré" : `${daysLeft}j`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SentinelPage;
